import React, { useRef, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  Chart as ChartInstance,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { IMetaDailyMetric } from "../interface/metaAds";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface PerformanceChartProps {
  data: IMetaDailyMetric[];
  height?: number | string;
  className?: string;
  currency?: string;
  currencySymbol?: string;
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({ data, height = 300, className = "", currency = "USD", currencySymbol = "$" }) => {
  const chartRef = useRef<ChartInstance<"line"> | null>(null);
  const spendLabel = `Spend (${currency})`;

  const chartData: ChartData<"line"> = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: spendLabel,
        data: data.map(d => d.spend_usd),
        borderColor: "#ef4444",
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        fill: true,
        tension: 0.4,
        yAxisID: "y",
      },
      {
        label: "Impressions",
        data: data.map(d => d.impressions),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        tension: 0.4,
        yAxisID: "y1",
      },
      {
        label: "Clicks",
        data: data.map(d => d.clicks),
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        fill: true,
        tension: 0.4,
        yAxisID: "y1",
      }
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#fff",
        titleColor: "#1f2937",
        bodyColor: "#4b5563",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const isSpend = context.dataset.label === spendLabel;
              label += isSpend
                ? `${currencySymbol}${context.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15, // Increased for better zoom granularity
          font: {
            size: 11,
          },
          callback: function (_value, index) {
            const label = this.getLabelForValue(index);
            if (label && typeof label === 'string') {
              // Handle YYYY-MM-DD or other formats
              const datePart = label.includes('T') ? label.split('T')[0] : label;
              const parts = datePart.split('-');
              if (parts.length === 3) {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const day = parts[2];
                const month = months[parseInt(parts[1]) - 1];
                return `${day} ${month}`;
              }
            }
            return label;
          }
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: spendLabel,
          font: { size: 10 }
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Volume',
          font: { size: 10 }
        },
        grid: {
          drawOnChartArea: true,
          color: "#f3f4f6"
        },
      },
    },
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    if (!chartArea) return;

    // Apply gradients
    const datasets = chart.data.datasets;
    const colors = ["#ef4444", "#3b82f6", "#10b981"];

    datasets.forEach((dataset, i) => {
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, `${colors[i]}33`);
      gradient.addColorStop(1, `${colors[i]}00`);
      dataset.backgroundColor = gradient;
    });

    chart.update();
  }, [data]);

  return (
    <div style={{ height }} className={className}>
      <Line ref={chartRef} data={chartData} options={options} />
    </div>
  );
};

export default PerformanceChart;
